import { Fragment, useState } from "react";
import {
  AlertTriangle,
  Apple,
  ArrowRight,
  CalendarPlus,
  Camera,
  Clock,
  Download,
  Home,
  Info,
  Link2,
  // Skygger for den innebygde `Map` uten aliaset, og `new Map(...)` under blir
  // da et kall på et ikon.
  Map as MapIcon,
  MapPin,
  Mountain,
  Navigation,
  Printer,
  RotateCcw,
  Route,
  Sparkles,
  Sun,
  Trash2,
  TriangleAlert,
  Umbrella,
} from "lucide-react";
import {
  daylightSummary,
  formatDate,
  formatDateShort,
  formatDuration,
  formatHours,
  formatTime,
  scoreColor,
  weatherEmoji,
  weatherLabel,
} from "../lib/format";
import { dictionaries } from "../lib/i18n";
import { TAG_COLOR, theme } from "../lib/theme";
import type { Itinerary, UiLang, Stop } from "../lib/types";
import {
  appleMapsUrl,
  dayRoute,
  dayToGpx,
  downloadFile,
  googleMapsUrl,
  itineraryToGpx,
} from "../lib/deeplinks";
import { AddStopPicker } from "./AddStopPicker";
import { AmenityList } from "./AmenityList";
import { ClarityBadge } from "./ClarityBadge";
import { ConnectionRow } from "./ConnectionRow";
import { DayConstraints } from "./DayConstraints";
import { FoldPanel } from "./FoldPanel";
import { DayRationaleNote } from "./DayRationaleNote";
import { ViewScoreDial } from "./ViewScoreDial";

interface Props {
  itinerary: Itinerary;
  activeDay: number;
  onDayChange: (index: number) => void;
  onSelectStop: (stop: Stop) => void;
  onExportCalendar: () => void;
  onCopyLink: () => void;
  copied: boolean;
  onAddStop: (stopId: string) => void;
  onRemoveStop: (stopId: string) => void;
  onResetAdjustments: () => void;
  /**
   * «Bare dette stedet», og veien tilbake til hele ruta. `null` slår det av.
   *
   * Se `focusStopId` i lib/types.ts.
   */
  onFocusStop: (stopId: string | null) => void;
  /** true mens en justert rute hentes på nytt. */
  adjusting: boolean;
  lang: UiLang;
  onHoverStop?: (stop: Stop | null) => void;
}

export function ItineraryBoard({
  itinerary,
  activeDay,
  onDayChange,
  onSelectStop,
  onExportCalendar,
  onCopyLink,
  copied,
  onAddStop,
  onRemoveStop,
  onResetAdjustments,
  onFocusStop,
  adjusting,
  lang,
  onHoverStop,
}: Props) {
  const t = dictionaries[lang];
  const day = itinerary.days[activeDay];

  /*
   * Dagens beste nordlyssjanse blant stoppene.
   *
   * Ett sted holder: du er der den natta, ikke alle stedene samtidig. Å vise
   * snittet ville trukket en klar natt i Tromsø ned av en overskyet i Bodø.
   */
  const bestAurora = day?.stops
    .filter((stop) => stop.aurora)
    .sort((a, b) => b.aurora!.chance - a.aurora!.chance)[0];
  const [hoveredStop, setHoveredStop] = useState<string | null>(null);
  const adjustments = itinerary.request.include.length + itinerary.request.exclude.length;

  /*
   * Det ene stedet planen kan handle om i stedet for ruta.
   *
   * Meldt fra bruk: «velger jeg bare ett stopp, hvorfor kan jeg ikke få en
   * rute bare for det? Jeg må ende opp med å planlegge hele ruta og fjerne de
   * andre stoppene». Det var en presis beskrivelse av det som fantes: å feste
   * ett sted ga fortsatt hele korridoren, og resten måtte lukes ut for hånd.
   *
   * Tilbudet står bare når nøyaktig ett sted er festet, fordi det er da
   * spørsmålet er entydig. To festede steder kunne betydd hvilken som helst av
   * dem, og en knapp som gjetter er verre enn ingen knapp.
   */
  /*
   * ── Avgangen man tar på slutten av dagen ─────────────────────────────────
   *
   * Meldt fra bruk: «toget jeg tar på dag 2 fra et stopp på dag 1 burde også
   * vises på dag 1».
   *
   * Og det var en presis beskrivelse av feilen. Etappene ligger på dagen de
   * fører *inn i*: `day.connections[0]` går fra gårsdagens siste stopp til
   * dagens første. Reisen man faktisk foretar seg på kvelden av dag 1 sto
   * derfor bare under dag 2 — og den som leste dag 1 fikk en dag som endte i
   * ingenting.
   *
   * Her hentes den fram igjen på riktig dag. Den står fortsatt på dag 2 også,
   * der den forteller hvordan man kom: «også», som meldingen sier.
   */
  const nextDay = itinerary.days[activeDay + 1];
  const leavingFrom = day?.stops.at(-1);
  const onward =
    nextDay && leavingFrom
      ? nextDay.connections.find((leg) => leg.fromStopId === leavingFrom.id)
      : undefined;
  /* Dit man kommer. `to` gir `ConnectionRow` høyden og navnet den trenger. */
  const onwardTo = onward
    ? nextDay?.stops.find((stop) => stop.id === onward.toStopId)
    : undefined;

  /*
   * Hvor mye som ligger bak «greit å vite»-linja.
   *
   * Tallet, ikke bare et «finnes det noe»: et sammendrag som sier «3 ting å
   * vite» forteller at det er verdt et klikk, mens en lukket dør uten tall er
   * en gjetning. Se `FoldPanel`.
   */
  const dayInfoCount =
    (day?.notes.length ?? 0) +
    (day?.legs.length ? 1 : 0) +
    (day?.fullDayHike ? 1 : 0) +
    (itinerary.ferries ?? []).filter((ferry) => ferry.dayNumber === day?.dayNumber).length +
    (itinerary.optimization?.applied ? 1 : 0);

  const focused = itinerary.request.focusStopId;
  const onlyPinned = itinerary.request.include.length === 1 ? itinerary.request.include[0] : null;
  const pinnedName = onlyPinned
    ? (itinerary.days.flatMap((entry) => entry.stops).find((stop) => stop.id === onlyPinned)?.name ??
      itinerary.alternatives.find((stop) => stop.id === onlyPinned)?.name ??
      null)
    : null;

  /*
   * Stoppene etappene peker på, slått opp på id.
   *
   * `dayRoute` er dagens stopp med gårsdagens siste foran — nøyaktig kjeden
   * serveren regnet etappene av, så begge ender finnes alltid her. Etappene
   * bærer bare navn og id; koordinatene til veibeskrivelsen ligger på stoppet.
   */
  const stopsOnRoute = new Map(dayRoute(itinerary, activeDay).map((stop) => [stop.id, stop]));
  // Adkomstadvarslene gjelder lengden, ikke veien — se ControlPanel.
  const largeVehicle = itinerary.request.vehicle === "bobil";
  if (!day) return null;

  return (
    /*
      `id` er landingspunktet for «velg ett sted» — se `scrollToPlan`. Meldt fra
      bruk: «velger jeg Trolltunga, hvorfor lander jeg her?», med et bilde av
      skjemaet. Den som er ferdig med å spørre, skal lande på svaret.
    */
    <div className="board" id="plan">
      <div className="board-tabs no-print" role="tablist" aria-label={t.bestDayCompare}>
        {itinerary.days.map((entry, index) => (
          <button
            key={entry.dayNumber}
            role="tab"
            aria-selected={index === activeDay}
            className={`tab ${index === activeDay ? "tab--on" : ""}`}
            onClick={() => onDayChange(index)}
          >
            {/*
              Reisedagen har et navn, ikke et nummer.

              «Dag 5» ved siden av fire dager med steder sier ingenting om at
              denne ene er noe annet. Nummeret står fortsatt i advarselen — «dag
              5 går med til å reise» — så koblingen er ikke borte.
            */}
            <span>{entry.homeward ? t.homewardTab : t.dayTab(entry.dayNumber)}</span>
            {entry.date && <span className="tab-date">{formatDateShort(entry.date, lang)}</span>}
          </button>
        ))}
      </div>

      <div className="board-body">
        <div className="day-header">
          <div>
            <div className="day-meta" style={{ marginBottom: 8 }}>
              {day.date && <span style={{ color: "#DCE9EE" }}>{formatDate(day.date, lang)}</span>}
              <span>
                <Route size={12} /> {day.distanceKm} km
              </span>
              <span>
                <Clock size={12} /> {formatHours(day.travelHours, lang)} {t.travelSuffix}
              </span>
              {day.daylight && (
                <span>
                  <Sun size={12} /> {daylightSummary(day.daylight, lang)}
                  {!day.daylight.midnightSun && !day.daylight.polarNight && ` (${day.daylight.hours} t)`}
                </span>
              )}
              {/*
                Fotolyset står ved siden av dagslyset, ikke i stedet for det. De
                to sier forskjellige ting: dagslyset er hvor lenge man kan være
                ute, fotolyset er når man bør stå på utsiktspunktet.
              */}
              {day.golden?.evening && (
                <span>
                  <Camera size={12} /> {t.goldenBest}{" "}
                  {new Date(day.golden.evening.from).toLocaleTimeString(
                    lang === "no" ? "nb-NO" : "en-GB",
                    { hour: "2-digit", minute: "2-digit" },
                  )}
                </span>
              )}
            </div>
          </div>
          {day.viewScore != null && day.viewLabel && (
            <ViewScoreDial
              score={day.viewScore}
              label={day.viewLabel}
              /*
               * Dagsscoren arves fra stoppene, så grunnlaget gjør det også: er
               * dagen regnet av klimanormaler, skal måleren si det. Alle
               * stoppene på en dag har samme grunnlag — det følger av datoen,
               * ikke av stedet — så det første er representativt.
               */
              basis={day.stops.find((stop) => stop.viewScore)?.viewScore?.basis}
              lang={lang}
            />
          )}
        </div>

        {/*
          Nordlyset hører hjemme her, ikke bare i detaljpanelet. «Ser jeg
          nordlys på dag 3?» er et spørsmål om dagen, ikke om ett stopp — og
          bak tre klikk fant ingen det.

          Dagens beste sjanse vises, siden det holder å være ett sted den natta.
        */}
        {bestAurora && (
          <div className={`day-aurora ${bestAurora.aurora!.chance >= 0.4 ? "day-aurora--good" : ""}`}>
            <Sparkles size={14} />
            <span className="day-aurora-text">
              <strong>
                {t.auroraTitle}: {t.auroraChance(Math.round(bestAurora.aurora!.chance * 100))}
              </strong>
              <small>
                {bestAurora.name} · {t.auroraKp(bestAurora.aurora!.kp, bestAurora.aurora!.requiredKp)}
                {bestAurora.aurora!.limitedBy && ` · ${t.auroraLimited[bestAurora.aurora!.limitedBy]}`}
              </small>
            </span>
          </div>
        )}

        {/*
          Det dagen ikke kan planlegges rundt, før stoppene.

          Fotturen, ferja og den stengte fjellovergangen sto tidligere i hver
          sin liste et annet sted på siden. De hører hjemme her, over dagens
          innhold: den som leser dag 4 må se at dag 4 har en ferje 16:20.
        */}
        {/*
          ── «Greit å vite», på én linje ──────────────────────────────────────

          Meldt fra bruk: «i hver dag er det mange stengte veier, så jeg må
          rulle helt til bunnen for å se reiseplanen min — legg det i en linje,
          eller bare som info, ikke bare vis det der».

          Og det stemte: fotturen, ferja, den stengte fjellovergangen,
          avgangstabellen og vintermerknadene sto alle sammen *over* stoppene.
          Den som ville se dagen sin, måtte forbi alt som kunne gå galt med den
          først.

          De er ikke fjernet — en ferje 16:20 er ikke pynt — men de er lagt
          sammen. Hodet teller hvor mange det er, så ingen dør står lukket uten
          at man vet at det er noe bak. Valget huskes under én nøkkel for alle
          dagene: åpner du den på dag 1, står den åpen på dag 4.
        */}
        {dayInfoCount > 0 && (
          <FoldPanel
            id="dayInfo"
            title={t.dayInfoTitle}
            icon={<Info size={14} />}
            summary={t.dayInfoSummary(dayInfoCount)}
          >
            <DayConstraints
              day={day}
              ferries={itinerary.ferries ?? []}
              alerts={itinerary.trafficAlerts ?? []}
              lang={lang}
            />

        {/*
          Hvorfor dagen ble slik, i optimalisererens egne tall. Vises bare når
          værstyringen faktisk kjørte og ga en endring — ellers finnes det
          ingen begrunnelse å gi.
        */}
        {itinerary.optimization?.applied && (
          <DayRationaleNote
            rationale={itinerary.optimization.days?.find((d) => d.dayNumber === day.dayNumber)}
            dayCount={itinerary.days.length}
            lang={lang}
          />
        )}

        {day.legs.length > 0 && (
          <div className="legs">
            <div className="legs-head">{t.departuresHeading}</div>
            {day.legs.map((leg, index) => (
              <div className="leg" key={`${leg.line}-${index}`}>
                <span className="leg-line">{leg.line}</span>
                <span className="leg-times">
                  {formatTime(leg.aimedDeparture, lang)} → {formatTime(leg.aimedArrival, lang)}
                </span>
                <span className="leg-route">
                  {leg.from} → {leg.to}
                </span>
                <span className="leg-duration">{formatDuration(leg.durationMinutes, lang)}</span>
              </div>
            ))}
          </div>
        )}

        {day.notes.length > 0 && (
          <ul className="day-notes">
            {day.notes.map((note) => (
              <li className="day-note" key={note}>
                <AlertTriangle size={14} color="#C79A3C" />
                <span>{note}</span>
              </li>
            ))}
          </ul>
        )}
          </FoldPanel>
        )}

        {/*
          Dagsetappen ut i bilen.
          
          Lenkene står per dag og ikke per tur, fordi det er dagen man kjører:
          en navigasjon fra Oslo til Bergen med tolv veipunkter er ikke noe
          Google Maps kan gjøre — den tar åtte — og ikke noe man vil ha.
        */}
        {(() => {
          const route = dayRoute(itinerary, activeDay);
          const google = googleMapsUrl(route);
          const apple = appleMapsUrl(route, itinerary.request.mode);
          if (!google && !apple) return null;

          return (
            <div className="day-navigate no-print">
              <span className="day-navigate-label">{t.navigateLabel}</span>
              {google && (
                <a className="ghost-button" href={google} target="_blank" rel="noreferrer">
                  <Navigation size={13} />
                  {t.openGoogleMaps}
                </a>
              )}
              {apple && (
                <a className="ghost-button" href={apple} target="_blank" rel="noreferrer">
                  <MapIcon size={13} />
                  {t.openAppleMaps}
                </a>
              )}
              <button
                className="ghost-button"
                onClick={() =>
                  downloadFile(
                    `${itinerary.corridor.id}-dag${activeDay + 1}.gpx`,
                    dayToGpx(itinerary, activeDay),
                    "application/gpx+xml",
                  )
                }
              >
                <Download size={13} />
                {t.exportGpxDay}
              </button>
            </div>
          );
        })()}

        {day.stops.map((stop, index) => {
          const isLast = index === day.stops.length - 1;
          /*
           * Etappen som fører *inn* til dette stoppet, tegnet i mellomrommet
           * over det. For dagens første stopp er det strekningen fra
           * gårsdagens siste — den kjøringen er en del av dagen, og manglet.
           */
          const arriving = day.connections.find((c) => c.toStopId === stop.id);
          const color = TAG_COLOR[stop.tags[0]] ?? theme.color.accent;
          // Oslo og Bergen er faste endepunkter i korridoren.
          /*
           * Endepunktene kan ikke fjernes, og det er `position` som sier hvilke
           * de er: 0 er korridorens startby, 1 er sluttbyen. Det er samme regel
           * serveren bruker, så knappen kan ikke tilby noe forespørselen avviser.
           *
           * Sto som `id !== "oslo" && id !== "bergen"` fra da Bergensbanen var
           * eneste rute. Etter at fem korridorer kom til, ga den en
           * fjern-knapp på Stavanger, Dombås, Trondheim, Bodø, Tromsø og
           * Ålesund — som serveren så nektet å utføre.
           */
          const isRemovable = stop.position > 0 && stop.position < 1;

          return (
            <Fragment key={stop.id}>
            {arriving && (
              <ConnectionRow
                connection={arriving}
                from={stopsOnRoute.get(arriving.fromStopId)}
                to={stopsOnRoute.get(arriving.toStopId)}
                lang={lang}
              />
            )}
            <div
              className="timeline-item"
              // Se StopHoverCard.
              data-hover-source=""
              onMouseEnter={() => {
                setHoveredStop(stop.id);
                onHoverStop?.(stop);
              }}
              onMouseLeave={() => {
                setHoveredStop(null);
                onHoverStop?.(null);
              }}
            >
              <div className="timeline-rail">
                <span
                  className="timeline-dot"
                  style={{ background: hoveredStop === stop.id ? theme.color.accent : color }}
                />
                {!isLast && <span className="timeline-line" />}
              </div>

              {/*
                Bildet av stedet, ved siden av teksten.
                
                Tidslinja var ren tekst i en app som har et fotografi av hvert
                eneste stopp. Miniatyren gjør dagen til noe man ser, ikke bare
                leser — og den er allerede bufret for offline bruk.
              */}
              {stop.image && (
                <button
                  className="stop-thumb no-print"
                  onClick={() => onSelectStop(stop)}
                  aria-label={stop.name}
                  tabIndex={-1}
                >
                  <img src={stop.image.src} alt="" loading="lazy" aria-hidden="true" />
                </button>
              )}

              {/*
                Hele raden åpner stoppet, ikke bare navnet.
                
                Før var navnet og miniatyren de eneste klikkbare flatene, mens
                blurben, taggene og tidene under dem — det meste av raden — var
                døde. Man leste beskrivelsen, ville vite mer, og klikket på
                teksten uten at noe skjedde.
                
                Klikk som kommer fra en knapp eller lenke inne i raden slipper
                gjennom til sin egen handling: fjern-knappen skal fjerne, ikke
                åpne. Navnet er fortsatt en ekte knapp, så tastaturbruk og
                skjermlesere er uendret — raden er en snarvei oppå det, ikke en
                erstatning for det.
              */}
              <div
                className="timeline-body timeline-body--clickable"
                onClick={(event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest("button, a, input, select")) return;
                  onSelectStop(stop);
                }}
              >
                <div className="stop-head">
                  <MapPin size={14} color={theme.color.accent} />
                  <button className="stop-name" onClick={() => onSelectStop(stop)}>
                    {stop.name}
                  </button>
                  {isRemovable && (
                    <button
                      className="stop-remove no-print"
                      onClick={() => onRemoveStop(stop.id)}
                      disabled={adjusting}
                      /*
                       * Oversettelsen fantes alt — `removeStop` står i alle
                       * fem ordbøkene — men knappen hadde teksten skrevet inn
                       * på norsk. `title` er en synlig tooltip, så en fransk
                       * bruker fikk «Fjern Flåm fra ruten» i klartekst.
                       */
                      aria-label={t.removeStop(stop.name)}
                      title={t.removeStop(stop.name)}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                  {stop.hoursFromStart > 0 && (
                    <span className="stop-time">
                      <Clock size={11} /> ~{formatHours(stop.hoursFromStart, lang)} {t.fromStart(itinerary.corridor.startName)}
                    </span>
                  )}
                  {stop.elevationM >= 500 && (
                    <span className="stop-time">
                      <Mountain size={11} /> {stop.elevationM} {t.unitMasl}
                    </span>
                  )}
                </div>

                {/*
                  ── Hva et stopp viser før man spør ──────────────────────────

                  Meldt fra bruk: «reiseplanen skal vise grunnleggende
                  informasjon, med resten som utvider seg hvis brukeren vil se
                  den», og «én knapp for å se mer om stedet burde åpne det
                  andre vi vet om stedet — vær, kamera og de tingene».

                  Her sto alt på én gang: merkelapper, vær, utsiktsindeks,
                  sikt, kjøretøyadvarsel, lokalmatmerke, hele tjenestelista og
                  en værmerknad. Per stopp. Med tre stopp på en dag ble dagen
                  en skjermlengde med merkelapper man måtte forbi for å se
                  hvor man skulle.

                  Igjen står de to tingene som svarer «hva er dette stedet»:
                  setningen om stedet, og advarselen om at noe er stengt —
                  den siste fordi den er en *beslutning*, ikke en opplysning.

                  Resten finnes allerede i skuffen, og har alltid gjort det.
                  Knappen under er den samme veien dit som navnet over; den
                  sier bare tydelig at veien finnes. Se StopDrawer.
                */}
                <p className="stop-blurb">{stop.blurb}</p>

                {largeVehicle && stop.vehicle && (
                  <p className="stop-warn">
                    <TriangleAlert size={12} />
                    {t.vehicleWarning}
                  </p>
                )}

                <button type="button" className="stop-more" onClick={() => onSelectStop(stop)}>
                  {t.stopMore}
                </button>
              </div>
            </div>
            </Fragment>
          );
        })}

        {/*
          Dagen som går med til å reise hjem.

          Sto ikke dette her, ville tavla vist «fri dag» på den dagen brukeren
          tilbringer i bilen — dagen har ingen stopp, og det er nettopp poenget.
          Etappen tegnes av `ConnectionRow`, den samme som alle andre etapper,
          så avgangene og ferjene kommer med uten at noe nytt måtte skrives.
        */}
        {day.homeward && itinerary.returnLeg && (
          <div className="homeward-day">
            <h3 className="homeward-title">
              <Home size={15} /> {t.homewardTitle(itinerary.returnLeg.toName)}
            </h3>
            <ConnectionRow connection={itinerary.returnLeg} lang={lang} />
            {/* `.homeward-note`, ikke `.note`: se stilarket for hvorfor. */}
            {day.notes.map((note) => (
              <p key={note} className="homeward-note">
                {note}
              </p>
            ))}
          </div>
        )}

        {day.stops.length === 0 && !day.homeward && (
          <p className="stop-blurb">{t.freeDay}</p>
        )}

        {/*
          ── Hvordan dagen ender ─────────────────────────────────────────────

          Se `onward` over. Samme rad som etappene inne i dagen, så den leses
          likt — og med en vei videre, slik at dagene henger sammen framfor å
          være atskilte faner. Det var den andre halvdelen av det som ble meldt:
          «det må flyte bedre mellom dagene».
        */}
        {onward && (
          <div className="day-onward">
            <h4 className="day-onward-title">
              <ArrowRight size={14} />
              {t.onwardTitle(onward.toName)}
            </h4>
            <ConnectionRow connection={onward} from={leavingFrom} to={onwardTo} lang={lang} />
            <button
              type="button"
              className="day-onward-next no-print"
              onClick={() => onDayChange(activeDay + 1)}
            >
              {t.onwardToDay(nextDay!.dayNumber)}
            </button>
          </div>
        )}

        {/*
          Hva planen nå er, med ord.

          Uten den ser en plan på tre dager rundt Trolltunga ut som en plan
          langs Stavanger–Bergen som mistet stoppene sine — og «hvor ble resten
          av ruta av?» er et rimeligere spørsmål å stille enn å måtte gjette.
        */}
        {focused && (
          <p className="focus-note">
            {t.focusNote(
              itinerary.days.flatMap((entry) => entry.stops).find((stop) => stop.id === focused)
                ?.name ?? itinerary.corridor.name,
            )}
          </p>
        )}

        <div className="adjust-row">
          <AddStopPicker
            alternatives={itinerary.alternatives}
            dayStops={day.stops}
            onAdd={onAddStop}
            /*
             * Bildekortet og detaljpanelet gjelder også stedene man vurderer å
             * legge inn, ikke bare de som alt er i ruta. Uten dette var «legg
             * til et stopp» en liste over navn en tilreisende ikke hadde noen
             * grunn til å kjenne — se AddStopPicker.
             */
            onHover={onHoverStop}
            onInspect={onSelectStop}
            lang={lang}
          />
          {focused ? (
            <button
              type="button"
              className="focus-swap no-print"
              onClick={() => onFocusStop(null)}
            >
              <Route size={13} />
              {t.focusWhole}
            </button>
          ) : (
            pinnedName &&
            onlyPinned && (
              <button
                type="button"
                className="focus-swap no-print"
                onClick={() => onFocusStop(onlyPinned)}
              >
                <MapPin size={13} />
                {t.focusOnly(pinnedName)}
              </button>
            )
          )}
          {adjustments > 0 && (
            <button className="reset-adjustments no-print" onClick={onResetAdjustments}>
              <RotateCcw size={12} />
              {t.resetAdjustments(adjustments)}
            </button>
          )}
        </div>
      </div>

      <div className="board-foot">
        <span>{itinerary.corridor.name}</span>
        <ArrowRight size={12} />
        <span>{t.travelModeName[itinerary.request.mode]}</span>
        <ArrowRight size={12} />
        <span>{t.daysOption(itinerary.request.days)}</span>
        <ArrowRight size={12} />
        <span>{t.stopCount(itinerary.totalStops)}</span>

        <span className="board-actions no-print">
          {/*
            GPX for hele turen. Appen har ingen veidata og skal ikke være en
            navigasjonsapp — men fila lar en GPS-klokke eller en Garmin overta.
          */}
          <button
            className="ghost-button"
            onClick={() =>
              downloadFile(
                `${itinerary.corridor.id}-${itinerary.request.days}d.gpx`,
                itineraryToGpx(itinerary),
                "application/gpx+xml",
              )
            }
          >
            <Download size={13} />
            {t.exportGpx}
          </button>
          <button className="ghost-button" onClick={onCopyLink}>
            <Link2 size={13} />
            {copied ? t.shared : t.share}
          </button>
          <button className="ghost-button" onClick={onExportCalendar} disabled={!itinerary.request.startDate}>
            <CalendarPlus size={13} />
            {t.calendar}
          </button>
          {/*
            «Skriv ut» sto det før, og de fleste vet ikke at utskriftsdialogen
            også kan lagre som PDF. Knappen sier det nå, for det er slik den
            faktisk brukes — planen havner på telefonen, ikke i en skriver.
          */}
          <button
            className="ghost-button"
            onClick={() => window.print()}
            title={t.roadbookPrint}
          >
            <Printer size={13} />
            {t.roadbookPrint}
          </button>
        </span>
      </div>
    </div>
  );
}
